import { useState, useEffect, useCallback } from 'react';
import { Tables } from '@/integrations/supabase/types';
import { useAuth } from '@/shared/contexts/AuthContext';
import { apiClient } from '@/shared/services/apiClient';
import { API_ROUTES } from '@/shared/services/apiRoutes';

type EndCustomer = Tables<'end_customers'>;

export interface Customer {
  id: string; // UUID from end_customers table
  name: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string;
  project: string | null;
  company_id: string | null;
  created_by: string | null;
  assigned_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCustomerInput {
  first_name: string;
  last_name: string;
  phone_number: string;
  email?: string | null;
  project?: string | null;
  assigned_to?: string | null;
}

export interface UpdateCustomerInput {
  name?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  email?: string | null;
  project?: string | null;
  assigned_to?: string | null;
}

export function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();

  /**
   * Fetch customers from backend or Supabase based on feature flag
   */
  const fetchCustomers = useCallback(async () => {
    if (!user) {
      setCustomers([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      setError(null);

      // SECURITY: Use backend API for all customer operations
      console.log('[useCustomers] Using backend API');

      const response = await apiClient.get<{
        ok: boolean;
        customers: Customer[];
        total: number;
      }>(API_ROUTES.CUSTOMERS);

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      setCustomers(response.customers || []);
    } catch (err) {
      console.error('[useCustomers] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch customers');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  /**
   * Refresh customers
   */
  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCustomers();
  }, [fetchCustomers]);

  /**
   * Create customer
   */
  const createCustomer = useCallback(
    async (input: CreateCustomerInput): Promise<Customer | null> => {
      if (!user) {
        setError('No authenticated user');
        return null;
      }

      try {
        setError(null);

        // SECURITY: Use backend API
        console.log('[useCustomers] Creating customer via backend');

        const response = await apiClient.post<{
          ok: boolean;
          customer: Customer;
        }>(API_ROUTES.CUSTOMERS, input);

        if (!response.ok || !response.customer) {
          throw new Error('Failed to create customer');
        }

        // Add to local state
        setCustomers(prev => [response.customer, ...prev]);
        return response.customer;
      } catch (err) {
        console.error('[useCustomers] Error creating:', err);
        if (!error) {
          setError(err instanceof Error ? err.message : 'Failed to create customer');
        }
        return null;
      }
    },
    [user, error]
  );

  /**
   * Update customer
   */
  const updateCustomer = useCallback(
    async (id: string, input: UpdateCustomerInput): Promise<Customer | null> => {
      if (!user) {
        setError('No authenticated user');
        return null;
      }

      try {
        setError(null);

        // SECURITY: Use backend API
        console.log('[useCustomers] Updating customer via backend');

        const response = await apiClient.put<{
          ok: boolean;
          customer: Customer;
        }>(API_ROUTES.CUSTOMER_BY_ID(id), input);

        if (!response.ok || !response.customer) {
          throw new Error('Failed to update customer');
        }

        // Update local state
        setCustomers(prev =>
          prev.map(c => c.id === id ? response.customer : c)
        );

        return response.customer;
      } catch (err) {
        console.error('[useCustomers] Error updating:', err);
        setError(err instanceof Error ? err.message : 'Failed to update customer');
        return null;
      }
    },
    [user]
  );

  /**
   * Delete customer
   */
  const deleteCustomer = useCallback(
    async (id: string): Promise<boolean> => {
      if (!user) {
        setError('No authenticated user');
        return false;
      }

      try {
        setError(null);

        // SECURITY: Use backend API
        console.log('[useCustomers] Deleting customer via backend');

        const response = await apiClient.delete<{
          ok: boolean;
          message: string;
        }>(API_ROUTES.CUSTOMER_BY_ID(id));

        if (!response.ok) {
          throw new Error('Failed to delete customer');
        }

        // Remove from local state
        setCustomers(prev => prev.filter(c => c.id !== id));
        return true;
      } catch (err) {
        console.error('[useCustomers] Error deleting:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete customer');
        return false;
      }
    },
    [user]
  );

  /**
   * Search customers locally - matches mobile app behavior
   * Searches across name, first_name, last_name, phone_number, and email
   */
  const searchCustomers = useCallback(
    (query: string, field: 'name' | 'phone_number' | 'email') => {
      if (!query.trim() || query.length < 2) return [];

      const queryLower = query.toLowerCase();

      return customers
        .filter(customer => {
          // Search across ALL fields for better UX (matching mobile)
          const nameMatch = customer.name && customer.name.toLowerCase().includes(queryLower);
          const firstNameMatch = customer.first_name && customer.first_name.toLowerCase().includes(queryLower);
          const lastNameMatch = customer.last_name && customer.last_name.toLowerCase().includes(queryLower);

          // Clean phone formatting and search
          const phoneMatch = customer.phone_number &&
            customer.phone_number.replace(/[\s\-\(\)\+]/g, '').toLowerCase().includes(
              queryLower.replace(/[\s\-\(\)\+]/g, '')
            );

          const emailMatch = customer.email && customer.email.toLowerCase().includes(queryLower);

          return nameMatch || firstNameMatch || lastNameMatch || phoneMatch || emailMatch;
        })
        .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
        .slice(0, 8); // Limit to 8 results (matching mobile)
    },
    [customers]
  );

  return {
    customers,
    loading,
    refreshing,
    error,
    fetchCustomers,
    refresh,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    searchCustomers,
    refetch: fetchCustomers, // Alias for backward compatibility
  };
}
